/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { ProjectState } from '../types.ts';

// The last AI provider the user explicitly picked (in project settings or any of
// the generate modals) is remembered globally so that the NEXT new project the
// user creates defaults to that same provider, rather than always falling back
// to the location heuristic. Persisted in localStorage (a single scalar), not in
// the per-project state, precisely because it must outlive any one project.
export type AiProvider = ProjectState['aiProvider'];

const STORAGE_KEY = 'aims.lastSelectedAiProvider';

// Providers that are still accepted in the union for backward-compatible
// deserialization of old projects, but that the UI no longer offers. We never
// want to resurrect one of these as a new-project default.
// 'openai-pro' (gpt-5.5) was retired on 2026-07-31 in favor of gpt-5.6-luna, which is
// ~25x cheaper per output token; the model id no longer passes the backend allowlist.
// 'openai' (gpt-5.3-chat-latest) was retired on 2026-08-10 for the same reason —
// gpt-5.6-luna is the same non-reasoning chat tier, cheaper, so the two-fast-tier
// picker was redundant. Both ids are gone from the backend allowlist.
// 'deepseek-pro' (deepseek-v4-pro) was retired on 2026-09-10: DeepSeek shipped
// V4.1 Flash as the single replacement for both v4-flash and v4-pro, so DeepSeek
// is one tier with no pro option. Its id is gone from the backend allowlist too.
// 'openai-mini' (gpt-5.4-mini) was removed from the pickers on 2026-10-28, leaving
// OpenAI as the single gpt-6-luna tier. The backend still accepts the id, since
// the prompt interpreters and Enhance call gpt-5.4-mini directly.
// 'grok' (xAI grok-4.5) was removed on 2026-10-28 as too costly ($2/$6 per 1M
// tokens); its callables and every server-side xAI path are deleted, so a legacy
// value lands on the default tier.
// 'gemini' (gemini-2.5-flash) was removed from the pickers on 2026-10-28, leaving
// Gemini as the single 3.8-flash tier, which keeps the persisted value 'gemini-pro'.
// The backend still accepts 2.5-flash: it is the server default for Enhance and
// the prompt interpreters.
const RETIRED_PROVIDERS: ReadonlySet<string> = new Set([
  'azure',
  'claude',
  'claude-opus',
  'openai-pro',
  'openai',
  'deepseek-pro',
  'openai-mini',
  'grok',
  'gemini',
]);

// A retired tier normally falls back to DEFAULT_AI_PROVIDER, but when the tier
// was retired *within* a family that still exists, switching the user to a
// different vendor is a bigger surprise than moving them to the surviving tier.
// Entries here take priority over the default. (Users outside the US default to
// DeepSeek deliberately — often because OpenAI is not reachable for them — so a
// 'deepseek-pro' project must stay on DeepSeek.) migrateRetiredAiProvider in
// ProjectUtil applies this to stored project state on load, so the legacy value
// is rewritten rather than merely reinterpreted on every read.
const RETIRED_PROVIDER_REPLACEMENTS: Readonly<Record<string, AiProvider>> = {
  'deepseek-pro': 'deepseek',
  'openai-mini': 'openai-luna',
  gemini: 'gemini-pro',
};

// The tier a user lands on when nothing else decides for them: no stored choice,
// or a stored choice that has since been retired. gpt-6-luna is the cheapest
// OpenAI tier ($0.10/$0.50 per 1M tokens) and the one PROJECT_DEFAULTS uses.
// (The 'openai-luna' value ran gpt-5.6-luna until 2026-10-28; it was upgraded in
// place, so no provider value was retired.)
export const DEFAULT_AI_PROVIDER: AiProvider = 'openai-luna';

// Map a stored provider that is no longer offered onto the default tier. Every
// picker and every call site funnels through this, so a retired model id can
// never reach the backend allowlist (which would reject it as invalid-argument).
// Note this does NOT rewrite the saved project — it only decides what the UI
// shows and which model we actually call for this session.
export const demoteRetiredProvider = (provider: string | undefined | null): AiProvider => {
  if (!provider) return DEFAULT_AI_PROVIDER;
  if (!RETIRED_PROVIDERS.has(provider)) return provider as AiProvider;
  return RETIRED_PROVIDER_REPLACEMENTS[provider] ?? DEFAULT_AI_PROVIDER;
};

// Record the user's explicit provider choice so future new projects inherit it.
export const saveAiProviderPreference = (provider: AiProvider): void => {
  if (!provider || RETIRED_PROVIDERS.has(provider)) return;
  try {
    localStorage.setItem(STORAGE_KEY, provider);
  } catch {
    // localStorage may be unavailable (private mode / storage disabled) — the
    // preference is a convenience, so silently fall back to the heuristic.
  }
};

// Read the remembered provider, or null if none was ever chosen (or storage is
// unavailable). Returns null for retired providers so callers use the heuristic.
export const loadAiProviderPreference = (): AiProvider | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && !RETIRED_PROVIDERS.has(stored)) return stored as AiProvider;
    // A remembered tier that has since been retired within a still-offered
    // family maps onto its survivor rather than being dropped entirely, and the
    // corrected value replaces the stale one so this runs at most once.
    const replacement = stored ? RETIRED_PROVIDER_REPLACEMENTS[stored] : undefined;
    if (replacement) {
      localStorage.setItem(STORAGE_KEY, replacement);
      return replacement;
    }
  } catch {
    // ignore
  }
  return null;
};
